import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Image as ImageIcon, MapPin, ArrowClockwise } from "@phosphor-icons/react";

const EMPTY = {
  salesman_mobile: "", retailer_id: "", payment_received: false,
  cheque_no: "", bank_name: "", cheque_date: "", cheque_photo_data: "",
  latitude: null, longitude: null, location_name: "",
  bill_date: "", bill_no: "", bill_amount: "",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
      { headers: { "Accept-Language": "en" } }
    );
    const j = await r.json();
    return j.display_name || "";
  } catch { return ""; }
}

export default function RetailerVisits() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [photoView, setPhotoView] = useState(null);
  const [locating, setLocating] = useState(false);
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data: v }, { data: r }, { data: s }] = await Promise.all([
      client.get("/visits"), client.get("/retailers"), client.get("/salesmen"),
    ]);
    setRows(v); setRetailers(r); setSalesmen(s);
  };
  useEffect(() => { load(); }, []);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const name = await reverseGeocode(lat, lng);
        setForm((f) => ({ ...f, latitude: lat, longitude: lng, location_name: name || `${lat.toFixed(5)}, ${lng.toFixed(5)}` }));
        setLocating(false);
        toast.success("Location captured");
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Location permission denied" : "Couldn't get location");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const openNew = () => {
    setForm(EMPTY); setEditId(null); setOpen(true);
    // Auto-capture location on new visit
    setTimeout(captureLocation, 300);
  };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    setForm({ ...form, cheque_photo_data: await fileToBase64(f) });
  };

  const save = async () => {
    if (!form.salesman_mobile || !form.retailer_id) return toast.error("Salesman and Retailer required");
    if (form.payment_received && (!form.cheque_no || !form.bank_name || !form.cheque_date)) {
      return toast.error("Fill cheque details");
    }
    try {
      const payload = {
        ...form,
        bill_amount: form.bill_amount === "" ? 0 : Number(form.bill_amount),
      };
      if (editId) await client.put(`/visits/${editId}`, payload); else await client.post("/visits", payload);
      toast.success(editId ? "Updated" : "Visit logged"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete visit?")) return;
    try { await client.delete(`/visits/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Retailer Visits" subtitle="Salesman field visits & payments"
        actions={<Button data-testid="visit-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> Log Visit</Button>}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Visit Time</th><th className="px-4 py-3">Salesman</th>
              <th className="px-4 py-3">Retailer</th><th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Bill No</th><th className="px-4 py-3">Bill Date</th>
              <th className="px-4 py-3">Bill Amount</th>
              <th className="px-4 py-3">Cheque No</th><th className="px-4 py-3">Bank</th>
              <th className="px-4 py-3">Cheque Date</th><th className="px-4 py-3">Photo</th>
              <th className="px-4 py-3">Entered By</th>{isAdmin && <th className="px-4 py-3">Actions</th>}
            </tr></thead>
            <tbody data-testid="visits-tbody">
              {rows.map(v=>(
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(v.visit_time).toLocaleString()}</td>
                  <td className="px-4 py-2">{v.salesman_name}</td>
                  <td className="px-4 py-2">{v.retailer_name}</td>
                  <td className="px-4 py-2 text-xs max-w-xs">
                    {v.location_name ? (
                      <a
                        href={v.latitude && v.longitude ? `https://www.google.com/maps?q=${v.latitude},${v.longitude}` : undefined}
                        target="_blank" rel="noreferrer"
                        className="text-orange-700 hover:underline inline-flex items-center gap-1"
                        title={v.location_name}
                      >
                        <MapPin size={12}/> <span className="truncate max-w-[200px] inline-block align-bottom">{v.location_name}</span>
                      </a>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${v.payment_received ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {v.payment_received ? "Received" : "None"}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono">{v.bill_no || "—"}</td>
                  <td className="px-4 py-2 font-mono">{v.bill_date || "—"}</td>
                  <td className="px-4 py-2 font-mono">{v.bill_amount ? Number(v.bill_amount).toFixed(2) : "—"}</td>
                  <td className="px-4 py-2 font-mono">{v.cheque_no || "—"}</td>
                  <td className="px-4 py-2">{v.bank_name || "—"}</td>
                  <td className="px-4 py-2 font-mono">{v.cheque_date || "—"}</td>
                  <td className="px-4 py-2">
                    {v.cheque_photo_data ? (
                      <button onClick={()=>setPhotoView(v.cheque_photo_data)} className="p-1 hover:bg-slate-100 rounded-sm"><ImageIcon size={16}/></button>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">{nameFor(v.entered_by)}</td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button onClick={()=>openEdit(v)} className="p-1.5 hover:bg-slate-100 rounded-sm"><PencilSimple size={14}/></button>
                        <button onClick={()=>remove(v.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={isAdmin?14:13} className="px-4 py-10 text-center text-slate-500">No visits logged</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Log"} Retailer Visit</DialogTitle></DialogHeader>
          <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-2">
            <div>
              <label className="label-cap">Salesman *</label>
              <Select value={form.salesman_mobile} onValueChange={(v)=>setForm({...form, salesman_mobile: v})}>
                <SelectTrigger data-testid="visit-salesman-select" className="rounded-sm"><SelectValue placeholder="Select salesman"/></SelectTrigger>
                <SelectContent>{salesmen.map(s=><SelectItem key={s.mobile} value={s.mobile}>{s.name} · {s.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Retailer *</label>
              <Select value={form.retailer_id} onValueChange={(v)=>setForm({...form, retailer_id: v})}>
                <SelectTrigger data-testid="visit-retailer-select" className="rounded-sm"><SelectValue placeholder="Select retailer"/></SelectTrigger>
                <SelectContent>{retailers.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="border border-orange-200 bg-orange-50 rounded-sm p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="label-cap text-orange-800">GPS Location</div>
                <button type="button" onClick={captureLocation} disabled={locating}
                        data-testid="visit-recapture-gps-btn"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:text-orange-900 disabled:opacity-50">
                  <ArrowClockwise size={12} weight="bold" className={locating ? "animate-spin" : ""}/> {locating ? "Locating…" : "Recapture"}
                </button>
              </div>
              <div className="text-sm text-orange-950 min-h-[1.25rem]" data-testid="visit-location-display">
                {form.location_name || (locating ? "Getting location…" : "—")}
              </div>
              {form.latitude && form.longitude && (
                <div className="text-xs text-orange-800/70 font-mono mt-1">
                  {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <div>
                <div className="label-cap">Payment Received</div>
                <div className="text-xs text-slate-500 mt-0.5">Toggle if payment collected</div>
              </div>
              <Switch data-testid="visit-payment-switch" checked={form.payment_received} onCheckedChange={(v)=>setForm({...form, payment_received: v})}/>
            </div>

            {form.payment_received && (
              <div className="grid gap-3 bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label-cap">Bill No</label><Input data-testid="visit-bill-no-input" value={form.bill_no} onChange={(e)=>setForm({...form, bill_no: e.target.value})}/></div>
                  <div><label className="label-cap">Bill Date</label><Input data-testid="visit-bill-date-input" type="date" value={form.bill_date} onChange={(e)=>setForm({...form, bill_date: e.target.value})}/></div>
                </div>
                <div><label className="label-cap">Bill Amount</label><Input data-testid="visit-bill-amount-input" type="number" step="0.01" value={form.bill_amount} onChange={(e)=>setForm({...form, bill_amount: e.target.value})}/></div>

                <div className="pt-2 mt-1 border-t border-emerald-200">
                  <div className="label-cap text-emerald-800">Cheque details *</div>
                </div>
                <div><label className="label-cap">Cheque No</label><Input data-testid="visit-cheque-no-input" value={form.cheque_no} onChange={(e)=>setForm({...form, cheque_no: e.target.value})}/></div>
                <div><label className="label-cap">Bank Name</label><Input data-testid="visit-bank-input" value={form.bank_name} onChange={(e)=>setForm({...form, bank_name: e.target.value})}/></div>
                <div><label className="label-cap">Cheque Date</label><Input data-testid="visit-cheque-date-input" type="date" value={form.cheque_date} onChange={(e)=>setForm({...form, cheque_date: e.target.value})}/></div>
                <div>
                  <label className="label-cap">Cheque Photo (optional)</label>
                  <Input data-testid="visit-cheque-photo-input" type="file" accept="image/*" onChange={onPhoto}/>
                  {form.cheque_photo_data && <img src={form.cheque_photo_data} alt="preview" className="mt-2 max-h-32 rounded-sm border border-emerald-200"/>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="visit-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoView} onOpenChange={(v)=>!v && setPhotoView(null)}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle>Cheque Photo</DialogTitle></DialogHeader>
          {photoView && <img src={photoView} alt="cheque" className="w-full rounded-sm border border-slate-200"/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
